/**
 * TanStack Query hooks for the notification feed.
 *
 * THE POINT OF THE SPLIT: the badge and the feed are two queries, not one.
 *
 * `useUnreadCount` polls a route that returns a single integer. It runs on a
 * timer for as long as the app is open, so it must stay the cheapest request
 * in the product — no titles rendered, no property names resolved, no payload
 * the user is not looking at.
 *
 * `useNotificationFeed` is `enabled`-gated and fetches only when something is
 * actually going to display it (the dropdown opening, or the full page
 * mounting). Polling the feed instead would do all of the above once a minute
 * per open tab, which is the mistake this file exists to avoid.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchFeed,
  fetchPushPrefs,
  fetchUnreadCount,
  markAllRead,
  markRead,
  resetPushPrefs,
  updatePushPrefs,
  type FeedResponse,
  type PushPrefsPatch,
} from "./api";

/** Domain-first inline keys, matching the repo's convention (no central map). */
export const NOTIFICATION_KEYS = {
  all: ["notifications"] as const,
  count: ["notifications", "unread-count"] as const,
  feed: (limit: number, unreadOnly: boolean) =>
    ["notifications", "feed", limit, unreadOnly] as const,
};

/**
 * How often the bell re-checks. Long enough that eight family members idling
 * on the dashboard cost nothing; short enough that a backup failure or a new
 * access request surfaces while somebody is still looking at the screen.
 */
export const POLL_INTERVAL_MS = 60_000;

export function useUnreadCount() {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.count,
    queryFn: fetchUnreadCount,
    refetchInterval: POLL_INTERVAL_MS,
    // The bell is chrome on every page; a transient blip must not surface as a
    // broken header, and the next tick recovers it anyway.
    retry: false,
    staleTime: POLL_INTERVAL_MS / 2,
  });
}

export function useNotificationFeed(limit: number, enabled: boolean, unreadOnly = false) {
  return useQuery<FeedResponse>({
    queryKey: NOTIFICATION_KEYS.feed(limit, unreadOnly),
    queryFn: () => fetchFeed(limit, unreadOnly),
    enabled,
  });
}

/**
 * Both mutations invalidate the WHOLE `["notifications"]` subtree.
 *
 * Marking one item read changes the badge, the dropdown's copy of the list and
 * the full page's copy — three caches over one server-side fact. Invalidating
 * the prefix keeps them from disagreeing, which is the failure users actually
 * notice (a badge that will not clear).
 */
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => markRead(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markAllRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all }),
  });
}

// ── Push prefs (Wave 6, B4) — per-type toggles, mute, reset ─────────────────

export const PUSH_PREFS_KEY = ["notifications", "push-prefs"] as const;

export function usePushPrefs(enabled = true) {
  return useQuery({
    queryKey: PUSH_PREFS_KEY,
    queryFn: fetchPushPrefs,
    enabled,
    staleTime: 30_000,
  });
}

/** One mutation for toggle / mute / clear-mute — all are the same PUT. */
export function useUpdatePushPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PushPrefsPatch) => updatePushPrefs(patch),
    onSuccess: (prefs) => qc.setQueryData(PUSH_PREFS_KEY, prefs),
  });
}

export function useResetPushPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: resetPushPrefs,
    onSuccess: (prefs) => qc.setQueryData(PUSH_PREFS_KEY, prefs),
  });
}
